// route_moderation.jsx
// Mi Ruta moderation — the operator surface for the routes people publish from
// the PR Local app and the comment threads under them. Four queues behind one
// screen, backed by the admin Go backend's route-moderation package:
//
//   Rutas               prlocal_routes.moderation_status  pending|approved|rejected
//   Comentarios         prlocal_route_comments.moderation_status  visible|hidden
//   Reportes de rutas   prlocal_route_reports        (what users flagged)
//   Reportes de coment. prlocal_route_comment_reports
//
// The queues share a summary strip so a moderator can see where the work is
// before picking a tab. Every write is one of four: a route's verdict, a
// comment's visible/hidden flag, a comment tombstone, or a verdict on a report
// (which can carry out the action it implies in the same call).
//
// The route detail is where a call actually gets made: the route as a reader
// sees it — stops, byline, counters — next to the reports against it and its
// whole thread, so the moderator is not judging a title in isolation.

import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ShieldCheck, Flag, MessageSquare, Route as RouteIcon, RefreshCw, Search,
  ChevronLeft, ChevronRight, Eye, EyeOff, Trash2, CheckCircle2, XCircle,
  Clock, AlertTriangle, MapPin, Heart, Bookmark, GitFork, MessageCircle,
  User, BadgeCheck, ExternalLink, Inbox, CornerDownRight,
} from 'lucide-react';
import { useRouteModeration } from '../../authentication_components/routeModerationContext';
import { Modal, inputClass, Button, Loading, EmptyState } from './prlocal_ui';

const PAGE_SIZE = 25;

// The four queues, in the order a moderator works them: content first, then the
// tickets users filed against it.
const TABS = [
  { id: 'routes', label: 'Rutas', icon: RouteIcon },
  { id: 'comments', label: 'Comentarios', icon: MessageSquare },
  { id: 'route_reports', label: 'Reportes de rutas', icon: Flag },
  { id: 'comment_reports', label: 'Reportes de comentarios', icon: Flag },
];

const ROUTE_STATUSES = [
  { id: 'pending', label: 'Pendiente' },
  { id: 'approved', label: 'Aprobada' },
  { id: 'rejected', label: 'Rechazada' },
];
const VISIBILITIES = [
  { id: 'public', label: 'Pública' },
  { id: 'unlisted', label: 'No listada' },
  { id: 'private', label: 'Privada' },
];
const REASON_LABELS = {
  spam: 'Spam',
  unsafe: 'Peligrosa',
  inaccurate: 'Inexacta',
  offensive: 'Ofensiva',
  other: 'Otra',
};
const STOP_TYPE_LABELS = {
  business: 'Negocio',
  event: 'Evento',
  natural_location: 'Aventura',
  viator_experience: 'Experiencia',
  custom: 'Personalizada',
};

const fmtInt = (n) => (n ?? 0).toLocaleString();
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-PR', { year: 'numeric', month: 'short', day: 'numeric' });
};
const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es-PR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const authorLabel = (row) => row?.author_display_name
  || (row?.author_handle ? `@${row.author_handle}` : null)
  || row?.owner_user_id
  || row?.user_id
  || '—';

// The detail endpoint returns a route's comments in one flat, chronological
// list. Reading a thread that way interleaves a reply with whatever else was
// posted in between, so re-order it here: each top-level comment followed by its
// own replies. Replies whose parent fell outside the page are kept at the end
// rather than dropped — a moderator must still be able to act on them.
function threadOrder(comments = []) {
  const replies = new Map();
  comments.forEach((c) => {
    if (!c.parent_comment_id) return;
    if (!replies.has(c.parent_comment_id)) replies.set(c.parent_comment_id, []);
    replies.get(c.parent_comment_id).push(c);
  });

  const ordered = [];
  const placed = new Set();
  comments.filter((c) => !c.parent_comment_id).forEach((parent) => {
    ordered.push(parent);
    placed.add(parent.comment_id);
    (replies.get(parent.comment_id) || []).forEach((reply) => {
      ordered.push(reply);
      placed.add(reply.comment_id);
    });
  });
  comments.forEach((c) => { if (!placed.has(c.comment_id)) ordered.push(c); });
  return ordered;
}

export default function RouteModeration() {
  const {
    fetchModerationSummary, fetchModerationFilters,
    fetchRoutes, fetchRouteDetail, setRouteModeration,
    fetchComments, setCommentModeration, deleteComment,
    fetchRouteReports, fetchCommentReports, resolveRouteReport, resolveCommentReport,
  } = useRouteModeration();

  const [tab, setTab] = useState('routes');
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState(null);
  // Bumped after every write so the open queue and the summary strip reload
  // together — a verdict changes both.
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // ── Route queue filters ──
  const [rStatus, setRStatus] = useState('pending');
  const [rVisibility, setRVisibility] = useState('');
  const [rCity, setRCity] = useState('');
  const [rReported, setRReported] = useState(false);
  const [rSort, setRSort] = useState('newest');
  const [rSearchInput, setRSearchInput] = useState('');
  const [rSearch, setRSearch] = useState('');
  const [rOffset, setROffset] = useState(0);
  const [routesData, setRoutesData] = useState({ routes: [], total_count: 0 });
  const [loadingRoutes, setLoadingRoutes] = useState(true);

  // ── Comment queue filters ──
  const [cStatus, setCStatus] = useState('');
  const [cReported, setCReported] = useState(true);
  const [cSort, setCSort] = useState('reports');
  const [cSearchInput, setCSearchInput] = useState('');
  const [cSearch, setCSearch] = useState('');
  const [cOffset, setCOffset] = useState(0);
  const [commentsData, setCommentsData] = useState({ comments: [], total_count: 0 });
  const [loadingComments, setLoadingComments] = useState(true);

  // ── Report queue filters (shared shape, one state per queue) ──
  const [rrResolved, setRrResolved] = useState('false');
  const [rrReason, setRrReason] = useState('');
  const [rrOffset, setRrOffset] = useState(0);
  const [routeReports, setRouteReports] = useState({ reports: [], total_count: 0 });
  const [loadingRouteReports, setLoadingRouteReports] = useState(true);

  const [crResolved, setCrResolved] = useState('false');
  const [crReason, setCrReason] = useState('');
  const [crOffset, setCrOffset] = useState(0);
  const [commentReports, setCommentReports] = useState({ reports: [], total_count: 0 });
  const [loadingCommentReports, setLoadingCommentReports] = useState(true);

  // ── Modals ──
  const [detail, setDetail] = useState(null);          // { route, stops, reports, comments }
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [verdictTarget, setVerdictTarget] = useState(null); // route awaiting a moderation verdict
  const [resolveTarget, setResolveTarget] = useState(null); // { kind: 'route'|'comment', report }
  const [deleteTarget, setDeleteTarget] = useState(null);   // comment awaiting a tombstone
  const [busy, setBusy] = useState(false);

  // Filter option lists (loaded once).
  useEffect(() => {
    fetchModerationFilters()
      .then((data) => setOptions(data || null))
      .catch(() => { /* dropdowns simply stay minimal */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Summary strip.
  useEffect(() => {
    let cancelled = false;
    fetchModerationSummary()
      .then((data) => { if (!cancelled) setSummary(data || null); })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Error al cargar el resumen'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  // Debounced free-text searches.
  useEffect(() => {
    const t = setTimeout(() => { setROffset(0); setRSearch(rSearchInput.trim()); }, 350);
    return () => clearTimeout(t);
  }, [rSearchInput]);
  useEffect(() => {
    const t = setTimeout(() => { setCOffset(0); setCSearch(cSearchInput.trim()); }, 350);
    return () => clearTimeout(t);
  }, [cSearchInput]);

  // Route queue.
  useEffect(() => {
    let cancelled = false;
    setLoadingRoutes(true);
    fetchRoutes({
      moderation_status: rStatus || undefined,
      visibility: rVisibility || undefined,
      city: rCity || undefined,
      search: rSearch || undefined,
      reported: rReported ? 'true' : undefined,
      sort: rSort,
      limit: PAGE_SIZE,
      offset: rOffset,
    })
      .then((data) => { if (!cancelled) setRoutesData(data || { routes: [], total_count: 0 }); })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Error al cargar rutas'); })
      .finally(() => { if (!cancelled) setLoadingRoutes(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rStatus, rVisibility, rCity, rSearch, rReported, rSort, rOffset, reloadKey]);

  // Comment queue.
  useEffect(() => {
    let cancelled = false;
    setLoadingComments(true);
    fetchComments({
      moderation_status: cStatus || undefined,
      search: cSearch || undefined,
      reported: cReported ? 'true' : undefined,
      sort: cSort,
      limit: PAGE_SIZE,
      offset: cOffset,
    })
      .then((data) => { if (!cancelled) setCommentsData(data || { comments: [], total_count: 0 }); })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Error al cargar comentarios'); })
      .finally(() => { if (!cancelled) setLoadingComments(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cStatus, cSearch, cReported, cSort, cOffset, reloadKey]);

  // Route-report queue.
  useEffect(() => {
    let cancelled = false;
    setLoadingRouteReports(true);
    fetchRouteReports({
      resolved: rrResolved || undefined,
      reason: rrReason || undefined,
      limit: PAGE_SIZE,
      offset: rrOffset,
    })
      .then((data) => { if (!cancelled) setRouteReports(data || { reports: [], total_count: 0 }); })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Error al cargar reportes de rutas'); })
      .finally(() => { if (!cancelled) setLoadingRouteReports(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rrResolved, rrReason, rrOffset, reloadKey]);

  // Comment-report queue.
  useEffect(() => {
    let cancelled = false;
    setLoadingCommentReports(true);
    fetchCommentReports({
      resolved: crResolved || undefined,
      reason: crReason || undefined,
      limit: PAGE_SIZE,
      offset: crOffset,
    })
      .then((data) => { if (!cancelled) setCommentReports(data || { reports: [], total_count: 0 }); })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Error al cargar reportes de comentarios'); })
      .finally(() => { if (!cancelled) setLoadingCommentReports(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crResolved, crReason, crOffset, reloadKey]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const openDetail = useCallback((routeId) => {
    setLoadingDetail(true);
    setDetail({ route: { route_id: routeId, title: 'Cargando…' }, stops: [], reports: [], comments: [] });
    fetchRouteDetail(routeId)
      .then((data) => setDetail(data || null))
      .catch((err) => { toast.error(err.message || 'Error al cargar la ruta'); setDetail(null); })
      .finally(() => setLoadingDetail(false));
  }, [fetchRouteDetail]);

  const applyRouteVerdict = async (routeId, status, note) => {
    setBusy(true);
    try {
      await setRouteModeration(routeId, status, note);
      const label = ROUTE_STATUSES.find((s) => s.id === status)?.label || status;
      toast.success(`Ruta marcada como ${label.toLowerCase()}`);
      setVerdictTarget(null);
      if (detail?.route?.route_id === routeId) {
        // Keep the open detail truthful rather than closing it out from under
        // the moderator mid-review.
        fetchRouteDetail(routeId).then(setDetail).catch(() => setDetail(null));
      }
      reload();
    } catch (err) {
      toast.error(err.message || 'No se pudo actualizar la ruta');
    } finally {
      setBusy(false);
    }
  };

  const toggleCommentVisibility = async (comment) => {
    const next = comment.moderation_status === 'hidden' ? 'visible' : 'hidden';
    setBusy(true);
    try {
      await setCommentModeration(comment.comment_id, next);
      toast.success(next === 'hidden' ? 'Comentario oculto' : 'Comentario visible de nuevo');
      if (detail?.route?.route_id === comment.route_id) {
        fetchRouteDetail(comment.route_id).then(setDetail).catch(() => {});
      }
      reload();
    } catch (err) {
      toast.error(err.message || 'No se pudo actualizar el comentario');
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteComment = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteComment(deleteTarget.comment_id);
      toast.success('Comentario eliminado');
      const routeId = deleteTarget.route_id;
      setDeleteTarget(null);
      if (detail?.route?.route_id === routeId) {
        fetchRouteDetail(routeId).then(setDetail).catch(() => {});
      }
      reload();
    } catch (err) {
      toast.error(err.message || 'No se pudo eliminar el comentario');
    } finally {
      setBusy(false);
    }
  };

  const submitResolve = async ({ verdict, applyAction, note }) => {
    if (!resolveTarget) return;
    setBusy(true);
    try {
      const { kind, report } = resolveTarget;
      const result = kind === 'route'
        ? await resolveRouteReport(report.report_id, { verdict, applyAction, note })
        : await resolveCommentReport(report.report_id, { verdict, applyAction });

      const closed = result?.resolved_count || 1;
      const extra = result?.applied_action === 'route_rejected' ? ' · ruta rechazada'
        : result?.applied_action === 'comment_hidden' ? ' · comentario oculto'
          : '';
      toast.success(`${closed} reporte${closed === 1 ? '' : 's'} cerrado${closed === 1 ? '' : 's'}${extra}`);
      setResolveTarget(null);
      if (detail?.route?.route_id && (report.route_id === detail.route.route_id)) {
        fetchRouteDetail(detail.route.route_id).then(setDetail).catch(() => {});
      }
      reload();
    } catch (err) {
      toast.error(err.message || 'No se pudo resolver el reporte');
    } finally {
      setBusy(false);
    }
  };

  const cityOptions = options?.cities || [];
  const reasonOptions = options?.reasons?.length ? options.reasons : Object.keys(REASON_LABELS);

  const tabCounts = useMemo(() => ({
    routes: summary?.pending_routes,
    comments: summary?.reported_comments,
    route_reports: summary?.open_route_reports,
    comment_reports: summary?.open_comment_reports,
  }), [summary]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Heading */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800">
            <ShieldCheck className="h-5 w-5 text-violet-600" /> Moderación de Rutas
          </h2>
          <p className="text-sm text-slate-500">
            Rutas y comentarios publicados por usuarios en Mi Ruta, y los reportes que abrieron.
          </p>
        </div>
        <Button variant="secondary" onClick={reload} title="Actualizar">
          <RefreshCw className="h-4 w-4" /> Actualizar
        </Button>
      </div>

      {/* Queue summary */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile label="Rutas pendientes" value={summary?.pending_routes} icon={Clock} accent />
        <StatTile label="Rutas reportadas" value={summary?.reported_routes} icon={Flag} />
        <StatTile label="Reportes de rutas" value={summary?.open_route_reports} icon={AlertTriangle} />
        <StatTile label="Reportes de coment." value={summary?.open_comment_reports} icon={AlertTriangle} />
        <StatTile label="Comentarios ocultos" value={summary?.hidden_comments} icon={EyeOff} />
        <StatTile label="Rutas públicas" value={summary?.public_routes} icon={RouteIcon} />
      </div>

      {/* Queue picker */}
      <div className="mb-5 flex flex-wrap items-center gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
        {TABS.map(({ id, label, icon: Icon }) => {
          const active = tab === id;
          const count = tabCounts[id];
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                active ? 'bg-violet-50 text-violet-700 ring-1 ring-violet-100' : 'text-slate-500 hover:bg-slate-50 hover:text-violet-600'
              }`}
            >
              <Icon className={`h-4 w-4 ${active ? 'text-violet-600' : 'text-slate-400'}`} />
              <span>{label}</span>
              {count > 0 && (
                <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold tabular-nums ${
                  active ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  {fmtInt(count)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Routes queue ─────────────────────────────────────────────────── */}
      {tab === 'routes' && (
        <Panel>
          <FilterRow>
            <SearchBox value={rSearchInput} onChange={setRSearchInput} placeholder="Buscar por título, resumen o municipio…" />
            <Select label="Estado" value={rStatus} onChange={(v) => { setROffset(0); setRStatus(v); }}
              options={[{ id: '', label: 'Todos' }, ...ROUTE_STATUSES]} />
            <Select label="Visibilidad" value={rVisibility} onChange={(v) => { setROffset(0); setRVisibility(v); }}
              options={[{ id: '', label: 'Todas' }, ...VISIBILITIES]} />
            {cityOptions.length > 0 && (
              <Select label="Municipio" value={rCity} onChange={(v) => { setROffset(0); setRCity(v); }}
                options={[{ id: '', label: 'Todos' }, ...cityOptions.map((c) => ({ id: c, label: c }))]} />
            )}
            <Select label="Orden" value={rSort} onChange={(v) => { setROffset(0); setRSort(v); }}
              options={[
                { id: 'newest', label: 'Más recientes' },
                { id: 'oldest', label: 'Más antiguas' },
                { id: 'reports', label: 'Más reportadas' },
                { id: 'likes', label: 'Más gustadas' },
                { id: 'comments', label: 'Más comentadas' },
              ]} />
            <Toggle label="Solo reportadas" checked={rReported} onChange={(v) => { setROffset(0); setRReported(v); }} />
          </FilterRow>

          {loadingRoutes ? <Loading label="Cargando rutas…" />
            : routesData.routes.length === 0
              ? <EmptyState icon={RouteIcon} title="Ninguna ruta en esta cola" hint="Cambia el estado o quita los filtros para ver más." />
              : (
                <div className="space-y-3">
                  {routesData.routes.map((route) => (
                    <RouteCard
                      key={route.route_id}
                      route={route}
                      onOpen={() => openDetail(route.route_id)}
                      onVerdict={() => setVerdictTarget(route)}
                    />
                  ))}
                </div>
              )}

          <Pager total={routesData.total_count} offset={rOffset} onOffset={setROffset} noun="ruta" nounPlural="rutas" />
        </Panel>
      )}

      {/* ── Comments queue ───────────────────────────────────────────────── */}
      {tab === 'comments' && (
        <Panel>
          <FilterRow>
            <SearchBox value={cSearchInput} onChange={setCSearchInput} placeholder="Buscar en el texto o en el título de la ruta…" />
            <Select label="Estado" value={cStatus} onChange={(v) => { setCOffset(0); setCStatus(v); }}
              options={[{ id: '', label: 'Todos' }, { id: 'visible', label: 'Visible' }, { id: 'hidden', label: 'Oculto' }]} />
            <Select label="Orden" value={cSort} onChange={(v) => { setCOffset(0); setCSort(v); }}
              options={[
                { id: 'reports', label: 'Más reportados' },
                { id: 'newest', label: 'Más recientes' },
                { id: 'oldest', label: 'Más antiguos' },
              ]} />
            <Toggle label="Solo reportados" checked={cReported} onChange={(v) => { setCOffset(0); setCReported(v); }} />
          </FilterRow>

          {loadingComments ? <Loading label="Cargando comentarios…" />
            : commentsData.comments.length === 0
              ? <EmptyState icon={MessageSquare} title="Ningún comentario en esta cola" hint="Quita el filtro de reportados para ver toda la conversación." />
              : (
                <div className="space-y-3">
                  {commentsData.comments.map((comment) => (
                    <CommentCard
                      key={comment.comment_id}
                      comment={comment}
                      busy={busy}
                      onToggle={() => toggleCommentVisibility(comment)}
                      onDelete={() => setDeleteTarget(comment)}
                      onOpenRoute={() => openDetail(comment.route_id)}
                    />
                  ))}
                </div>
              )}

          <Pager total={commentsData.total_count} offset={cOffset} onOffset={setCOffset} noun="comentario" nounPlural="comentarios" />
        </Panel>
      )}

      {/* ── Route reports ────────────────────────────────────────────────── */}
      {tab === 'route_reports' && (
        <Panel>
          <FilterRow>
            <Select label="Estado" value={rrResolved} onChange={(v) => { setRrOffset(0); setRrResolved(v); }}
              options={[{ id: 'false', label: 'Abiertos' }, { id: 'true', label: 'Resueltos' }, { id: '', label: 'Todos' }]} />
            <Select label="Motivo" value={rrReason} onChange={(v) => { setRrOffset(0); setRrReason(v); }}
              options={[{ id: '', label: 'Todos' }, ...reasonOptions.map((r) => ({ id: r, label: REASON_LABELS[r] || r }))]} />
          </FilterRow>

          {loadingRouteReports ? <Loading label="Cargando reportes…" />
            : routeReports.reports.length === 0
              ? <EmptyState icon={Inbox} title="Sin reportes de rutas" hint="Nada pendiente en esta cola." />
              : (
                <div className="space-y-3">
                  {routeReports.reports.map((report) => (
                    <ReportCard
                      key={report.report_id}
                      report={report}
                      target={(
                        <button onClick={() => openDetail(report.route_id)} className="text-left font-semibold text-slate-800 hover:text-violet-700">
                          {report.route_title}
                        </button>
                      )}
                      badge={<StatusBadge status={report.route_moderation_status} />}
                      onResolve={() => setResolveTarget({ kind: 'route', report })}
                    />
                  ))}
                </div>
              )}

          <Pager total={routeReports.total_count} offset={rrOffset} onOffset={setRrOffset} noun="reporte" nounPlural="reportes" />
        </Panel>
      )}

      {/* ── Comment reports ──────────────────────────────────────────────── */}
      {tab === 'comment_reports' && (
        <Panel>
          <FilterRow>
            <Select label="Estado" value={crResolved} onChange={(v) => { setCrOffset(0); setCrResolved(v); }}
              options={[{ id: 'false', label: 'Abiertos' }, { id: 'true', label: 'Resueltos' }, { id: '', label: 'Todos' }]} />
            <Select label="Motivo" value={crReason} onChange={(v) => { setCrOffset(0); setCrReason(v); }}
              options={[{ id: '', label: 'Todos' }, ...reasonOptions.map((r) => ({ id: r, label: REASON_LABELS[r] || r }))]} />
          </FilterRow>

          {loadingCommentReports ? <Loading label="Cargando reportes…" />
            : commentReports.reports.length === 0
              ? <EmptyState icon={Inbox} title="Sin reportes de comentarios" hint="Nada pendiente en esta cola." />
              : (
                <div className="space-y-3">
                  {commentReports.reports.map((report) => (
                    <ReportCard
                      key={report.report_id}
                      report={report}
                      target={(
                        <div>
                          <p className="whitespace-pre-wrap break-words text-sm text-slate-700">
                            {report.comment_is_deleted
                              ? <span className="italic text-slate-400">Comentario eliminado</span>
                              : report.comment_body || <span className="italic text-slate-400">Sin texto</span>}
                          </p>
                          <button onClick={() => openDetail(report.route_id)} className="mt-1 flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-violet-700">
                            <RouteIcon className="h-3 w-3" /> {report.route_title}
                          </button>
                        </div>
                      )}
                      badge={<CommentStatusBadge status={report.comment_moderation_status} deleted={report.comment_is_deleted} />}
                      onResolve={() => setResolveTarget({ kind: 'comment', report })}
                    />
                  ))}
                </div>
              )}

          <Pager total={commentReports.total_count} offset={crOffset} onOffset={setCrOffset} noun="reporte" nounPlural="reportes" />
        </Panel>
      )}

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      <RouteDetailModal
        detail={detail}
        loading={loadingDetail}
        busy={busy}
        onClose={() => setDetail(null)}
        onVerdict={(route) => setVerdictTarget(route)}
        onToggleComment={toggleCommentVisibility}
        onDeleteComment={setDeleteTarget}
      />

      <VerdictModal
        route={verdictTarget}
        busy={busy}
        onClose={() => setVerdictTarget(null)}
        onSubmit={applyRouteVerdict}
      />

      <ResolveModal
        target={resolveTarget}
        busy={busy}
        onClose={() => setResolveTarget(null)}
        onSubmit={submitResolve}
      />

      <Modal
        open={!!deleteTarget}
        title="Eliminar comentario"
        subtitle="El texto se borra para siempre. La fila queda como lápida para no romper las respuestas."
        onClose={() => setDeleteTarget(null)}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={busy}>Cancelar</Button>
            <Button variant="danger" onClick={confirmDeleteComment} disabled={busy}>
              <Trash2 className="h-4 w-4" /> Eliminar
            </Button>
          </>
        )}
      >
        {deleteTarget && (
          <div className="space-y-3">
            <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">{deleteTarget.body || '—'}</p>
            <p className="text-xs text-slate-500">
              Por <span className="font-semibold">{authorLabel(deleteTarget)}</span> en «{deleteTarget.route_title}».
              Si solo quieres quitarlo de la vista, usa <span className="font-semibold">ocultar</span>: eso se puede deshacer.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Route detail ─────────────────────────────────────────────────────────────

function RouteDetailModal({ detail, loading, busy, onClose, onVerdict, onToggleComment, onDeleteComment }) {
  if (!detail) return null;
  const { route, stops = [], reports = [] } = detail;
  const openReports = reports.filter((r) => !r.report_resolved);
  const comments = threadOrder(detail.comments || []);

  return (
    <Modal
      open
      wide
      title={route?.title || 'Ruta'}
      subtitle={[authorLabel(route), route?.primary_city, route?.route_date && fmtDate(route.route_date)].filter(Boolean).join(' · ')}
      onClose={onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Cerrar</Button>
          <Button onClick={() => onVerdict(route)} disabled={busy || loading}>
            <ShieldCheck className="h-4 w-4" /> Cambiar estado
          </Button>
        </>
      )}
    >
      {loading ? <Loading label="Cargando ruta…" /> : (
        <div className="space-y-5">
          {/* Status + counters */}
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={route?.moderation_status} />
            <VisibilityBadge visibility={route?.visibility} />
            {route?.author_is_verified && (
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">
                <BadgeCheck className="h-3 w-3" /> Autor verificado
              </span>
            )}
            {openReports.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700">
                <Flag className="h-3 w-3" /> {openReports.length} reporte{openReports.length === 1 ? '' : 's'} abierto{openReports.length === 1 ? '' : 's'}
              </span>
            )}
          </div>

          {route?.summary && <p className="text-sm text-slate-600">{route.summary}</p>}

          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
            <Counter icon={MapPin} value={route?.stop_count} label="paradas" />
            <Counter icon={Heart} value={route?.like_count} label="me gusta" />
            <Counter icon={Bookmark} value={route?.save_count} label="guardadas" />
            <Counter icon={GitFork} value={route?.fork_count} label="copias" />
            <Counter icon={MessageCircle} value={route?.comment_count} label="comentarios" />
            <Counter icon={Eye} value={route?.view_count} label="vistas" />
          </div>

          {route?.moderation_note && (
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-3 text-sm text-amber-900">
              <span className="font-semibold">Nota de moderación:</span> {route.moderation_note}
              {route.moderated_at && <span className="ml-1 text-xs text-amber-700">({fmtDateTime(route.moderated_at)})</span>}
            </div>
          )}

          {/* Stops */}
          <DetailSection title={`Paradas (${stops.length})`} icon={MapPin}>
            {stops.length === 0 ? <p className="text-sm text-slate-400">Esta ruta no tiene paradas.</p> : (
              <ol className="space-y-2">
                {stops.map((stop) => (
                  <li key={stop.stop_id} className="flex gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">
                      {stop.position + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-800">{stop.stop_title}</p>
                      <p className="text-xs text-slate-500">
                        {[STOP_TYPE_LABELS[stop.stop_type] || stop.stop_type, stop.stop_city, stop.stop_subtitle].filter(Boolean).join(' · ')}
                      </p>
                      {stop.note && <p className="mt-1 text-xs italic text-slate-500">“{stop.note}”</p>}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </DetailSection>

          {/* Reports against the route */}
          <DetailSection title={`Reportes (${reports.length})`} icon={Flag}>
            {reports.length === 0 ? <p className="text-sm text-slate-400">Nadie ha reportado esta ruta.</p> : (
              <ul className="space-y-2">
                {reports.map((r) => (
                  <li key={r.report_id} className="rounded-xl border border-slate-100 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <ReasonBadge reason={r.report_reason} />
                      {r.report_resolved
                        ? <span className="text-xs font-semibold text-emerald-600">Resuelto · {r.report_verdict === 'upheld' ? 'confirmado' : 'descartado'}</span>
                        : <span className="text-xs font-semibold text-rose-600">Abierto</span>}
                      <span className="text-xs text-slate-400">{fmtDateTime(r.created_at)}</span>
                    </div>
                    {r.report_detail && <p className="mt-1.5 text-sm text-slate-600">{r.report_detail}</p>}
                  </li>
                ))}
              </ul>
            )}
          </DetailSection>

          {/* Thread */}
          <DetailSection title={`Conversación (${comments.length})`} icon={MessageSquare}>
            {comments.length === 0 ? <p className="text-sm text-slate-400">Todavía no hay comentarios.</p> : (
              <ul className="space-y-2">
                {comments.map((c) => (
                  <li key={c.comment_id} className={c.depth > 0 ? 'ml-6' : ''}>
                    <div className={`rounded-xl border p-3 ${c.moderation_status === 'hidden' ? 'border-slate-200 bg-slate-50' : 'border-slate-100'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            {c.depth > 0 && <CornerDownRight className="h-3 w-3 text-slate-300" />}
                            <span className="font-semibold text-slate-700">{authorLabel(c)}</span>
                            <span>{fmtDateTime(c.created_at)}</span>
                            {c.is_edited && <span className="text-slate-400">(editado)</span>}
                            <CommentStatusBadge status={c.moderation_status} deleted={c.is_deleted} />
                            {c.open_reports > 0 && (
                              <span className="inline-flex items-center gap-1 font-semibold text-rose-600">
                                <Flag className="h-3 w-3" /> {c.open_reports}
                              </span>
                            )}
                          </div>
                          <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-slate-700">
                            {c.is_deleted ? <span className="italic text-slate-400">Comentario eliminado</span> : c.body}
                          </p>
                        </div>
                        {!c.is_deleted && (
                          <div className="flex flex-shrink-0 gap-1">
                            <IconButton
                              title={c.moderation_status === 'hidden' ? 'Volver a mostrar' : 'Ocultar'}
                              onClick={() => onToggleComment(c)}
                              disabled={busy}
                            >
                              {c.moderation_status === 'hidden' ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </IconButton>
                            <IconButton title="Eliminar" danger onClick={() => onDeleteComment(c)} disabled={busy}>
                              <Trash2 className="h-4 w-4" />
                            </IconButton>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </DetailSection>
        </div>
      )}
    </Modal>
  );
}

// ── Verdict / resolve modals ─────────────────────────────────────────────────

function VerdictModal({ route, busy, onClose, onSubmit }) {
  const [status, setStatus] = useState('approved');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (route) {
      setStatus(route.moderation_status === 'approved' ? 'rejected' : 'approved');
      setNote(route.moderation_note || '');
    }
  }, [route]);

  if (!route) return null;

  return (
    <Modal
      open
      title="Estado de moderación"
      subtitle={route.title}
      onClose={onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button
            variant={status === 'rejected' ? 'danger' : 'primary'}
            onClick={() => onSubmit(route.route_id, status, note)}
            disabled={busy}
          >
            {status === 'rejected' ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />} Guardar
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-2">
          {ROUTE_STATUSES.map((s) => (
            <button
              key={s.id}
              onClick={() => setStatus(s.id)}
              className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${
                status === s.id
                  ? 'border-violet-300 bg-violet-50 text-violet-700 ring-1 ring-violet-200'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Nota de moderación (opcional)
          </span>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            placeholder="Por qué se tomó esta decisión. La ve el equipo, no el autor."
            className={inputClass}
          />
        </label>

        {status === 'rejected' && (
          <p className="rounded-xl border border-rose-100 bg-rose-50 p-3 text-xs text-rose-800">
            Una ruta rechazada desaparece del feed, del tablero de tendencias y de su enlace compartido.
          </p>
        )}
        {status === 'approved' && (
          <p className="rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-xs text-emerald-800">
            Una ruta aprobada aparece en el feed público y puede entrar al tablero de tendencias.
          </p>
        )}
      </div>
    </Modal>
  );
}

function ResolveModal({ target, busy, onClose, onSubmit }) {
  const [verdict, setVerdict] = useState('upheld');
  const [applyAction, setApplyAction] = useState(true);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (target) { setVerdict('upheld'); setApplyAction(true); setNote(''); }
  }, [target]);

  if (!target) return null;
  const { kind, report } = target;
  const isRoute = kind === 'route';

  return (
    <Modal
      open
      title="Resolver reporte"
      subtitle={isRoute ? report.route_title : `Comentario en «${report.route_title}»`}
      onClose={onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button
            variant={verdict === 'upheld' && applyAction ? 'danger' : 'primary'}
            onClick={() => onSubmit({ verdict, applyAction, note })}
            disabled={busy}
          >
            <CheckCircle2 className="h-4 w-4" /> Resolver
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div className="rounded-xl bg-slate-50 p-3">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <ReasonBadge reason={report.report_reason} />
            <span className="text-xs text-slate-400">{fmtDateTime(report.created_at)}</span>
          </div>
          {report.report_detail
            ? <p className="text-sm text-slate-700">{report.report_detail}</p>
            : <p className="text-sm italic text-slate-400">El reportante no dejó detalles.</p>}
          {!isRoute && (
            <p className="mt-2 border-t border-slate-200 pt-2 text-sm text-slate-600">
              {report.comment_is_deleted
                ? <span className="italic text-slate-400">Comentario eliminado</span>
                : report.comment_body}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setVerdict('upheld')}
            className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${
              verdict === 'upheld' ? 'border-rose-300 bg-rose-50 text-rose-700 ring-1 ring-rose-200' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            Confirmar reporte
          </button>
          <button
            onClick={() => setVerdict('dismissed')}
            className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${
              verdict === 'dismissed' ? 'border-emerald-300 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            Descartar
          </button>
        </div>

        {verdict === 'upheld' && (
          <>
            <Toggle
              label={isRoute ? 'Rechazar la ruta también' : 'Ocultar el comentario también'}
              checked={applyAction}
              onChange={setApplyAction}
            />
            {isRoute && applyAction && (
              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Nota de moderación (opcional)
                </span>
                <textarea
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={1000}
                  placeholder="Queda guardada en la ruta."
                  className={inputClass}
                />
              </label>
            )}
          </>
        )}

        <p className="text-xs text-slate-500">
          La decisión cierra <span className="font-semibold">todos</span> los reportes abiertos
          {isRoute ? ' de esta ruta' : ' de este comentario'}: es una sola llamada sobre el mismo contenido.
        </p>
      </div>
    </Modal>
  );
}

// ── Cards ────────────────────────────────────────────────────────────────────

function RouteCard({ route, onOpen, onVerdict }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <button onClick={onOpen} className="truncate text-left text-base font-bold text-slate-800 hover:text-violet-700">
              {route.title}
            </button>
            <StatusBadge status={route.moderation_status} />
            <VisibilityBadge visibility={route.visibility} />
            {route.open_reports > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-bold text-rose-700">
                <Flag className="h-3 w-3" /> {route.open_reports}
              </span>
            )}
          </div>

          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              <User className="h-3 w-3" /> {authorLabel(route)}
              {route.author_is_verified && <BadgeCheck className="h-3 w-3 text-sky-500" />}
            </span>
            {route.primary_city && <span>· {route.primary_city}</span>}
            <span>· {fmtDate(route.published_at || route.created_at)}</span>
          </p>

          {route.summary && <p className="mt-1.5 line-clamp-2 text-sm text-slate-600">{route.summary}</p>}

          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
            <Counter icon={MapPin} value={route.stop_count} label="paradas" />
            <Counter icon={Heart} value={route.like_count} label="me gusta" />
            <Counter icon={MessageCircle} value={route.comment_count} label="comentarios" />
            <Counter icon={Eye} value={route.view_count} label="vistas" />
          </div>
        </div>

        <div className="flex flex-shrink-0 gap-2">
          <Button variant="secondary" onClick={onOpen}>
            <ExternalLink className="h-4 w-4" /> Revisar
          </Button>
          <Button onClick={onVerdict}>
            <ShieldCheck className="h-4 w-4" /> Estado
          </Button>
        </div>
      </div>
    </div>
  );
}

function CommentCard({ comment, busy, onToggle, onDelete, onOpenRoute }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            {comment.depth > 0 && <CornerDownRight className="h-3 w-3 text-slate-300" />}
            <span className="font-semibold text-slate-700">{authorLabel(comment)}</span>
            <span>{fmtDateTime(comment.created_at)}</span>
            {comment.is_edited && <span className="text-slate-400">(editado)</span>}
            <CommentStatusBadge status={comment.moderation_status} deleted={comment.is_deleted} />
            {comment.open_reports > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 font-bold text-rose-700">
                <Flag className="h-3 w-3" /> {comment.open_reports}
              </span>
            )}
          </div>

          <p className="whitespace-pre-wrap break-words text-sm text-slate-700">
            {comment.is_deleted ? <span className="italic text-slate-400">Comentario eliminado</span> : comment.body}
          </p>

          <button onClick={onOpenRoute} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-violet-700">
            <RouteIcon className="h-3 w-3" /> {comment.route_title}
          </button>
        </div>

        {!comment.is_deleted && (
          <div className="flex flex-shrink-0 gap-2">
            <Button variant="secondary" onClick={onToggle} disabled={busy}>
              {comment.moderation_status === 'hidden'
                ? <><Eye className="h-4 w-4" /> Mostrar</>
                : <><EyeOff className="h-4 w-4" /> Ocultar</>}
            </Button>
            <Button variant="danger" onClick={onDelete} disabled={busy}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ReportCard({ report, target, badge, onResolve }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <ReasonBadge reason={report.report_reason} />
            {badge}
            {report.report_resolved ? (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                <CheckCircle2 className="h-3 w-3" />
                {report.report_verdict === 'upheld' ? 'Confirmado' : 'Descartado'}
                {report.resolved_at && <span className="font-normal text-slate-400">· {fmtDateTime(report.resolved_at)}</span>}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600">
                <AlertTriangle className="h-3 w-3" /> Abierto
              </span>
            )}
            <span className="text-xs text-slate-400">{fmtDateTime(report.created_at)}</span>
          </div>

          <div className="min-w-0">{target}</div>

          {report.report_detail && (
            <p className="mt-1.5 rounded-lg bg-slate-50 p-2 text-sm text-slate-600">“{report.report_detail}”</p>
          )}
        </div>

        {!report.report_resolved && (
          <div className="flex-shrink-0">
            <Button onClick={onResolve}>
              <ShieldCheck className="h-4 w-4" /> Resolver
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Presentational pieces ────────────────────────────────────────────────────

function Panel({ children }) {
  return <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 shadow-sm sm:p-5">{children}</div>;
}

function FilterRow({ children }) {
  return (
    <div className="mb-5 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      {children}
    </div>
  );
}

function SearchBox({ value, onChange, placeholder }) {
  return (
    <label className="relative block min-w-[16rem] flex-1">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Buscar</span>
      <Search className="pointer-events-none absolute bottom-2.5 left-3 h-4 w-4 text-slate-400" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`${inputClass} pl-9`}
      />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={`${inputClass} w-44`}>
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex cursor-pointer select-none items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-400"
      />
      {label}
    </label>
  );
}

function StatTile({ label, value, icon: Icon, accent }) {
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${accent ? 'border-violet-100 bg-violet-50/60' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        {Icon && <Icon className={`h-4 w-4 ${accent ? 'text-violet-500' : 'text-slate-300'}`} />}
      </div>
      <div className="mt-2 text-2xl font-extrabold tabular-nums text-slate-800">
        {value === undefined || value === null
          ? <span className="inline-block h-7 w-12 animate-pulse rounded bg-slate-100" />
          : fmtInt(value)}
      </div>
    </div>
  );
}

function DetailSection({ title, icon: Icon, children }) {
  return (
    <div>
      <h4 className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500">
        {Icon && <Icon className="h-3.5 w-3.5 text-violet-500" />} {title}
      </h4>
      {children}
    </div>
  );
}

function Counter({ icon: Icon, value, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <Icon className="h-3.5 w-3.5 text-slate-400" />
      <span className="font-semibold tabular-nums text-slate-600">{fmtInt(value)}</span> {label}
    </span>
  );
}

function IconButton({ children, title, onClick, disabled, danger }) {
  return (
    <button
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg p-2 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        danger ? 'text-slate-400 hover:bg-rose-50 hover:text-rose-600' : 'text-slate-400 hover:bg-violet-50 hover:text-violet-600'
      }`}
    >
      {children}
    </button>
  );
}

// Status is never colour-alone: every badge carries its own word.
function StatusBadge({ status }) {
  const map = {
    pending: { label: 'Pendiente', className: 'bg-amber-50 text-amber-700', Icon: Clock },
    approved: { label: 'Aprobada', className: 'bg-emerald-50 text-emerald-700', Icon: CheckCircle2 },
    rejected: { label: 'Rechazada', className: 'bg-rose-50 text-rose-700', Icon: XCircle },
  };
  const s = map[status];
  if (!s) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${s.className}`}>
      <s.Icon className="h-3 w-3" /> {s.label}
    </span>
  );
}

function CommentStatusBadge({ status, deleted }) {
  if (deleted) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
        <Trash2 className="h-3 w-3" /> Eliminado
      </span>
    );
  }
  if (status === 'hidden') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700">
        <EyeOff className="h-3 w-3" /> Oculto
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
      <Eye className="h-3 w-3" /> Visible
    </span>
  );
}

function VisibilityBadge({ visibility }) {
  const label = VISIBILITIES.find((v) => v.id === visibility)?.label;
  if (!label) return null;
  return (
    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{label}</span>
  );
}

function ReasonBadge({ reason }) {
  return (
    <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-violet-700">
      {REASON_LABELS[reason] || reason}
    </span>
  );
}

function Pager({ total, offset, onOffset, noun, nounPlural }) {
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));
  if (!total) return null;
  return (
    <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3 text-sm text-slate-500">
      <span>{fmtInt(total)} {total === 1 ? noun : nounPlural} · página {page} de {pageCount}</span>
      <div className="flex items-center gap-1">
        <button
          disabled={offset === 0}
          onClick={() => onOffset(Math.max(0, offset - PAGE_SIZE))}
          className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"
          aria-label="Página anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          disabled={page >= pageCount}
          onClick={() => onOffset(offset + PAGE_SIZE)}
          className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"
          aria-label="Página siguiente"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
